import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { subscribeToUserCollection } from '../utils/userDataHelper';
import { num } from './utils/costCalculations';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/sales-analytics.css';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import { normalizeRecipe } from '../utils/recipeSchema';
import { aggregateProductionUsage } from '../utils/workflowPlanning';
import useCurrencyTools from '../hooks/useCurrencyTools';

/**
 * UsageReports - Track ingredient usage patterns and costs
 * Route: /reports
 */

function UsageReports() {
  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('usage');
  const [timeRange, setTimeRange] = useState('all'); // all, month, quarter, year
  const { currencyCode } = useAuth();
  const currency = currencyCode || 'INR';
  const { convertMoney } = useCurrencyTools();
  const formatMoney = (value, fractionDigits = 0) =>
    formatCurrency(num(value, 0), currency, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

  // Load all data
  useEffect(() => {
    const unsubs = [];

    try {
      unsubs.push(subscribeToUserCollection('ingredients', (data) => {
        setIngredients(data);
      }));

      unsubs.push(subscribeToUserCollection('recipes', (data) => {
        setRecipes(data);
      }));

      unsubs.push(subscribeToUserCollection('batches', (data) => {
        setBatches(data);
        setLoading(false);
      }));

      unsubs.push(subscribeToUserCollection('sales', (data) => {
        setSales(data);
      }));
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }

    return () => unsubs.forEach(u => u());
  }, []);

  // Filter data by time range
  const filterByTime = useCallback((items, dateField = 'createdAt') => {
    if (timeRange === 'all') return items;
    
    const now = new Date();
    let cutoff;
    switch (timeRange) {
      case 'month':
        cutoff = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case 'quarter':
        cutoff = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case 'year':
        cutoff = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      default:
        return items;
    }
    
    return items.filter(item => {
      const date = item[dateField] ? new Date(item[dateField]) : null;
      return date && date >= cutoff;
    });
  }, [timeRange]);

  // Actual production movements only: saved recipes do not consume stock.
  const ingredientUsage = useMemo(() => aggregateProductionUsage(filterByTime(batches, 'madeDate'), ingredients, (value, code) => convertMoney(value, code, currency)), [ingredients, batches, filterByTime, convertMoney, currency]);

  // Cost analysis
  const costAnalysis = useMemo(() => {
    const filteredBatches = filterByTime(batches, 'madeDate');
    const filteredSales = filterByTime(sales, 'date');
    
    const totalProductionCost = filteredBatches.reduce((sum, b) => 
      sum + convertMoney(num(b.totalCost ?? (b.costPerBar * b.bars), 0), b.currencyCode || 'INR', currency), 0);
    
    const totalRevenue = filteredSales.reduce((sum, s) => sum + convertMoney(num(s.totalAmount, 0), s.currencyCode || 'INR', currency), 0);
    const totalProfit = filteredSales.reduce((sum, s) => sum + convertMoney(num(s.profit, 0), s.currencyCode || 'INR', currency), 0);
    
    // Cost breakdown by category
    const categoryBreakdown = {};
    ingredientUsage.forEach(ing => {
      const cat = ing.category.toLowerCase();
      if (!categoryBreakdown[cat]) {
        categoryBreakdown[cat] = { name: cat, totalCost: 0, itemCount: 0 };
      }
      categoryBreakdown[cat].totalCost += ing.totalCost;
      categoryBreakdown[cat].itemCount++;
    });

    return {
      totalProductionCost,
      totalRevenue,
      totalProfit,
      profitMargin: totalRevenue > 0 ? (totalProfit / totalRevenue * 100) : 0,
      categoryBreakdown: Object.values(categoryBreakdown).sort((a, b) => b.totalCost - a.totalCost),
      avgCostPerRecipe: filteredBatches.length > 0 ? totalProductionCost / filteredBatches.length : 0,
    };
  }, [batches, sales, ingredientUsage, filterByTime, convertMoney, currency]);

  // Recipe complexity analysis
  const recipeAnalysis = useMemo(() => {
    return recipes.map(recipe => {
      const oils = normalizeRecipe(recipe).items;
      const additives = recipe.additives || [];
      
      return {
        name: recipe.name,
        id: recipe.id,
        oilCount: oils.length,
        additiveCount: additives.length,
        totalIngredients: oils.length + additives.length,
        batchMass: num(recipe.computedBatchMass || recipe.batchSize, 0),
        costPerBar: convertMoney(num(recipe.totals?.perBar, 0), recipe.currencyCode || 'INR', currency),
        collection: recipe.collection || 'none',
        rating: recipe.rating || 0,
        createdAt: recipe.createdAt,
      };
    }).sort((a, b) => b.totalIngredients - a.totalIngredients);
  }, [recipes, convertMoney, currency]);

  // Stock alerts
  const stockAlerts = useMemo(() => {
    return ingredientUsage
      .filter(ing => {
        const avgUsagePerBatch = ing.totalGrams / Math.max(1, filterByTime(batches, 'madeDate').filter(b => b.inventoryApplied === true).length);
        const batchesRemaining = ing.currentStock / Math.max(1, avgUsagePerBatch);
        return batchesRemaining < 5 && ing.recipeCount > 0;
      })
      .map(ing => ({
        ...ing,
        avgUsagePerBatch: ing.totalGrams / Math.max(1, filterByTime(batches, 'madeDate').filter(b => b.inventoryApplied === true).length),
        batchesRemaining: ing.currentStock / Math.max(1, ing.totalGrams / Math.max(1, filterByTime(batches, 'madeDate').filter(b => b.inventoryApplied === true).length)),
      }));
  }, [ingredientUsage, batches, filterByTime]);

  if (loading) {
    return (
      <>
        <div className="sl-page reports-page">
          <div className="pageheader borderleft">
            <Breadcrumbs items={[
              { label: 'Home', to: '/' },
              { label: 'Reports' }
            ]} />
            <div className="pageheading">Usage Reports</div>
            <div className="pagedescription">Analyzing data...</div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="sl-page reports-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Reports' }
          ]} />
          <div className="pageheading">Usage Reports</div>
          <div className="pagedescription">Actual production usage and cost analysis. Legacy and record-only batches are excluded from ingredient consumption.</div>
        </div>

        <div className="reports-page-content borderleft">
          {/* Time range + tabs */}
          <section className="mypadding borderbottom">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
              <div className="rpt-tabs">
                {[
                  { id: 'usage', label: 'Ingredient Usage' },
                  { id: 'cost', label: 'Cost Analysis' },
                  { id: 'recipes', label: 'Recipe Analysis' },
                  { id: 'alerts', label: 'Stock Alerts' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`rpt-tab${activeTab === tab.id ? ' active' : ''}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <select className="sal-select" value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                <option value="all">All Time</option>
                <option value="month">Last Month</option>
                <option value="quarter">Last 3 Months</option>
                <option value="year">Last Year</option>
              </select>
            </div>
          </section>

          {/* Tab Content */}
          <section className="mypadding">
            {/* ═══ USAGE TAB ═══ */}
            {activeTab === 'usage' && (
              <div>
                <h3 style={{ margin: '0 0 16px', fontSize: '1.05em' }}>Most Used Ingredients</h3>
                <div className="rpt-table-wrap" style={{ marginBottom: 20 }}>
                  <table className="rpt-table">
                    <thead>
                      <tr>
                        <th>Ingredient</th>
                        <th className="center">Category</th>
                        <th className="center">Used In</th>
                        <th className="center">Usage source</th>
                        <th className="right">Total Grams</th>
                        <th className="right">Total Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ingredientUsage.slice(0, 15).map((ing, i) => {
                        const catClass = ing.category.includes('oil') ? 'oil' : ing.category.includes('fragrance') ? 'fragrance' : ing.category.includes('colour') ? 'colour' : ing.category.includes('alkali') ? 'alkali' : 'additive';
                        return (
                          <tr key={ing.id || ing.name}>
                            <td style={{ fontWeight: 500 }}>
                              {ing.name}
                            </td>
                            <td className="center">
                              <span className={`rpt-badge ${catClass}`}>{ing.category}</span>
                            </td>
                            <td className="center" style={{ fontWeight: 600 }}>{ing.recipeCount} batches</td>
                            <td className="center">{ing.recipeCount ? 'Production' : '—'}</td>
                            <td className="right">{ing.totalGrams.toFixed(0)}g</td>
                            <td className="right" style={{ color: '#16a34a', fontWeight: 500 }}>{formatMoney(ing.totalCost, 2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Unused Ingredients */}
                {ingredientUsage.filter(i => i.recipeCount === 0).length > 0 && (
                  <div className="rpt-unused-box">
                    <div className="title">Ingredients with no recorded production usage</div>
                    <div className="rpt-unused-tags">
                      {ingredientUsage.filter(i => i.recipeCount === 0).map(ing => (
                        <span key={ing.id || ing.name}>{ing.name}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ═══ COST TAB ═══ */}
            {activeTab === 'cost' && (
              <div>
                {/* Summary metrics */}
                <div className="rpt-stats" style={{ marginBottom: 20 }}>
                  <div className="rpt-metric-card blue">
                    <div className="label">Total Production Cost</div>
                    <div className="value">{formatMoney(costAnalysis.totalProductionCost, 0)}</div>
                  </div>
                  <div className="rpt-metric-card green">
                    <div className="label">Total Revenue</div>
                    <div className="value">{formatMoney(costAnalysis.totalRevenue, 0)}</div>
                  </div>
                  <div className="rpt-metric-card orange">
                    <div className="label">Total Profit</div>
                    <div className="value">{formatMoney(costAnalysis.totalProfit, 0)}</div>
                  </div>
                  <div className="rpt-metric-card purple">
                    <div className="label">Profit Margin</div>
                    <div className="value">{costAnalysis.profitMargin.toFixed(1)}%</div>
                  </div>
                </div>

                {/* Cost by Category */}
                <div className="rpt-panel">
                  <h4>Cost by Ingredient Category</h4>
                  {costAnalysis.categoryBreakdown.map((cat, i) => {
                    const maxCost = costAnalysis.categoryBreakdown[0]?.totalCost || 1;
                    const percentage = (cat.totalCost / maxCost) * 100;
                    return (
                      <div key={cat.name} className="rpt-bar-row">
                        <div className="rpt-bar-label">
                          <span className="name">{cat.name}</span>
                          <span className="amount">{formatMoney(cat.totalCost, 2)}</span>
                        </div>
                        <div className="rpt-bar-track">
                          <div className="fill" style={{ width: `${percentage}%`, background: `hsl(${200 - i * 30}, 70%, 50%)` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Most Expensive */}
                <div className="rpt-panel">
                  <h4>Highest Cost Ingredients</h4>
                  {[...ingredientUsage]
                    .sort((a, b) => b.totalCost - a.totalCost)
                    .slice(0, 10)
                    .map(ing => (
                      <div key={ing.id || ing.name} className="rpt-exp-row">
                        <span>{ing.name}</span>
                        <span className="cost">{formatMoney(ing.totalCost, 2)}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* ═══ RECIPES TAB ═══ */}
            {activeTab === 'recipes' && (
              <div>
                <div className="rpt-stats" style={{ marginBottom: 20 }}>
                  <div className="rpt-stat-card" style={{ background: '#eff6ff' }}>
                    <div className="value" style={{ color: '#2563eb' }}>{recipes.length}</div>
                    <div className="label">Total Recipes</div>
                  </div>
                  <div className="rpt-stat-card" style={{ background: '#f0fdf4' }}>
                    <div className="value" style={{ color: '#16a34a' }}>
                      {(recipeAnalysis.reduce((s, r) => s + r.totalIngredients, 0) / Math.max(1, recipeAnalysis.length)).toFixed(1)}
                    </div>
                    <div className="label">Avg Ingredients</div>
                  </div>
                  <div className="rpt-stat-card" style={{ background: '#fff7ed' }}>
                    <div className="value" style={{ color: '#ea580c' }}>
                      {formatMoney((recipeAnalysis.reduce((s, r) => s + r.costPerBar, 0) / Math.max(1, recipeAnalysis.length)), 0)}
                    </div>
                    <div className="label">Avg Cost/Bar</div>
                  </div>
                </div>

                <div className="rpt-table-wrap">
                  <table className="rpt-table">
                    <thead>
                      <tr>
                        <th>Recipe</th>
                        <th className="center">Oils</th>
                        <th className="center">Additives</th>
                        <th className="center">Total</th>
                        <th className="center">Collection</th>
                        <th className="center">Rating</th>
                        <th className="right">Cost/Bar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipeAnalysis.map(recipe => {
                        const colClass = recipe.collection === 'standard' ? 'standard' : recipe.collection === 'favorites' ? 'favorites' : 'none';
                        return (
                          <tr key={recipe.id}>
                            <td style={{ fontWeight: 500 }}>{recipe.name}</td>
                            <td className="center">{recipe.oilCount}</td>
                            <td className="center">{recipe.additiveCount}</td>
                            <td className="center" style={{ fontWeight: 600 }}>{recipe.totalIngredients}</td>
                            <td className="center">
                              <span className={`rpt-badge ${colClass}`}>{recipe.collection || 'none'}</span>
                            </td>
                            <td className="center">
                              <span className="rpt-stars">
                                {'★'.repeat(recipe.rating)}{'☆'.repeat(5 - recipe.rating)}
                              </span>
                            </td>
                            <td className="right" style={{ fontWeight: 500 }}>{formatMoney(recipe.costPerBar, 2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ═══ ALERTS TAB ═══ */}
            {activeTab === 'alerts' && (
              <div>
                {stockAlerts.length === 0 ? (
                  <div className="rpt-alert-healthy">
                    <div className="title">All stock levels are healthy!</div>
                    <div className="subtitle">Based on your usage patterns, no ingredients need restocking soon.</div>
                  </div>
                ) : (
                  <div>
                    <div className="rpt-alert-warning">
                      <strong>{stockAlerts.length} ingredient(s)</strong> may run out within the next 5 batches based on your usage patterns.
                    </div>
                    
                    {stockAlerts.map(ing => (
                      <div key={ing.id || ing.name} className="rpt-stock-card">
                        <div>
                          <div className="name">{ing.name}</div>
                          <div className="details">
                            Current: {ing.currentStock.toFixed(0)}g &bull; Avg use: {ing.avgUsagePerBatch.toFixed(0)}g/batch
                          </div>
                        </div>
                        <div>
                          <div className={`remaining ${ing.batchesRemaining < 2 ? 'critical' : 'warning'}`}>
                            ~{ing.batchesRemaining.toFixed(1)} batches
                          </div>
                          <div className="remaining-label">remaining</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      <Footer />
      </div>
    </>
  );
}

export default UsageReports;
